import { OrderBig } from '@chordcommerce/react-autonomy';
import { FormEvent, useEffect, useState } from 'react';
import { Text, Box, Grid, Button } from 'theme-ui';
import CheckoutButton from '~/components/Cart/CheckoutButton';
import PromoCode from '~/components/Cart/PromoCode';
import formatMoney from '~/utils/format-money';
// import GiftCardRedemptionForm from './GiftCardRedemptionForm';
import { useCart } from '@chordcommerce/react-autonomy';
import { useCalculateShipping } from '~/hooks/components/use-calculate-shipping';
import { getAllCartPromotionsForDisplay } from '~/utils/promotions';
import CloseIcon from '~/assets/images/icons/x.svg';
import SpinnerIcon from '~/components/Spinner/SpinnerIcon';

type Props = {
  apiError: string | null;
  stockError: string[];
  handleCartSubmit: (e: FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  cart: Partial<OrderBig>;
};

const CartSummary = ({ apiError, stockError, handleCartSubmit, isLoading, cart }: Props) => {
  const { itemTotal, displayItemTotal, adjustmentTotal, totalApplicableGiftCard } = cart;
  const [cartTotal, setCartTotal] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [isRemovingPromoCode, setIsRemovingPromoCode] = useState<boolean>(false);
  const { removeGiftCard, removePromoCode, isFetching } = useCart();
  const hasGiftCardPayment = !!cart.giftCardPayments?.length && cart.giftCardPayments?.length > 0;

  const promotions = getAllCartPromotionsForDisplay(cart);

  const { shippingAmount, displayShippingAmount } = useCalculateShipping();

  useEffect(() => {
    setCartTotal(
      Number(itemTotal ?? 0) + shippingAmount + Number(adjustmentTotal ?? 0) - Number(totalApplicableGiftCard ?? 0)
    );
  }, [shippingAmount, itemTotal, adjustmentTotal, totalApplicableGiftCard]);

  const handleRemoveGiftCard = async (redemptionCode: string | null | undefined) => {
    if (!redemptionCode) return;
    try {
      await removeGiftCard({
        order: { number: cart.number ?? '', token: cart.token ?? '' },
        redemptionCode,
      });
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : 'Unknown error');
    }
  };

  const handleRemovePromoCode = async (promoCode: string | null | undefined) => {
    if (!promoCode) return;
    try {
      setIsRemovingPromoCode(true);
      await removePromoCode({ promoCode });
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsRemovingPromoCode(false);
    }
  };

  return (
    <Box as="fieldset" sx={{ padding: 0, pt: '0' }}>
      {cart?.lineItems?.length && cart?.lineItems?.length > 0 ? (
        <Grid sx={{ gap: '1.2rem', justifyItems: 'start', mb: '1.6rem' }}>
          {/* <GiftCardRedemptionForm /> */}
          <PromoCode />
        </Grid>
      ) : null}
      <Grid
        sx={{
          rowGap: '1.2rem',
          mb: '2.8rem',
          fontSize: '1.2rem',
          whiteSpace: 'nowrap',
          fontWeight: 400,
          lineHeight: '100%',
        }}
      >
        <div
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            fontWeight: 700,
            textTransform: 'uppercase',
          }}
        >
          <span>Subtotal</span>
          <span>{displayItemTotal}</span>
        </div>

        {promotions &&
          promotions.map((p) => {
            return (
              <div key={p.id} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <div sx={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Button
                    variant="bare"
                    aria-label="Remove Promo Code"
                    onClick={() => handleRemovePromoCode(p.code)}
                    sx={{
                      textTransform: 'capitalize',
                      minWidth: '1.2rem',
                      minHeight: '1.2rem',
                      color: 'white',
                      svg: {
                        height: '1.2rem',
                        width: '1.2rem',
                      },
                    }}
                  >
                    {isRemovingPromoCode ? <SpinnerIcon size={12} /> : <CloseIcon />}
                  </Button>

                  <span>Promo - {p.code || p.label}</span>
                </div>
                <span>{p.displayAmount}</span>
              </div>
            );
          })}

        {hasGiftCardPayment &&
          cart.giftCardPayments?.map((giftCard) => (
            <div key={giftCard.id} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <div sx={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <Button
                  variant="bare"
                  aria-label="Remove Gift Card"
                  onClick={() => handleRemoveGiftCard(giftCard.redemptionCode)}
                  sx={{
                    textTransform: 'capitalize',
                    minWidth: '1.2rem',
                    minHeight: '1.2rem',
                    color: 'white',
                    svg: {
                      height: '1.2rem',
                      width: '1.2rem',
                    },
                  }}
                >
                  {isFetching ? <SpinnerIcon size={12} /> : <CloseIcon />}
                </Button>
                <span sx={{ textTransform: 'capitalize' }}>Gift Card #{giftCard.redemptionCode}</span>
              </div>
              <span>
                -{formatMoney(giftCard.amount, { locale: 'en', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          ))}
        <div sx={{ display: 'flex', justifyContent: 'space-between', textTransform: 'uppercase', fontWeight: 500 }}>
          <span>Shipping (based on weight)</span>
          <span>{displayShippingAmount}</span>
        </div>
      </Grid>
      <Box>
        <Text
          as="p"
          variant="h5"
          sx={{
            display: 'flex',
            flexFlow: 'row nowrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: ['2rem', '1.6rem'],
            mb: '2.8rem',
          }}
        >
          <span>Total</span>
          <span>{formatMoney(cartTotal, { locale: 'en', minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </Text>
        <form onSubmit={handleCartSubmit}>
          <CheckoutButton apiError={apiError || error} isLoading={isLoading || isFetching} stockError={stockError} />
        </form>
      </Box>
    </Box>
  );
};

export default CartSummary;
